import React from 'react';
import * as Yup from 'yup';
import { Divider, Stack } from '@mui/material';
import { isDefined, isEmptyString } from 'lib/typeguards';
import { TextFormField } from 'lib/form/fields/TextFormField';
import logger from 'lib/logger';
import { LocationSettingsApi } from 'components/location/LocationSettingsApi';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getPublicAndPrivateSettings } from 'store/settings/settingsSlice';
import { EditableSettingsSection } from './EditableSettingsSection';
import { FieldStatusRow, LabeledField } from './SetupSectionPrimitives';

interface FormValues {
    appStoreUrl: string;
    playStoreUrl: string;
    bundleIdentifier: string;
}

interface AppStoreDetailsSectionProps {
    isReadOnly?: boolean;
}

const isSet = (value?: string) => isDefined(value) && !isEmptyString(value);

const validationSchema = Yup.object().shape({
    appStoreUrl: Yup.string().trim().required('App Store URL is required.'),
    playStoreUrl: Yup.string().trim().required('Play Store URL is required.'),
    bundleIdentifier: Yup.string().trim().required('Bundle Identifier is required.')
});

export const AppStoreDetailsSection: React.FC<AppStoreDetailsSectionProps> = ({ isReadOnly = false }) => {
    const settings = useAppSelector(state => state.settings.settings);
    const { claims } = useAppSelector(state => state.auth);
    const dispatch = useAppDispatch();

    const appStoreUrl = settings?.app?.appStoreUrl;
    const playStoreUrl = settings?.app?.playStoreUrl;
    const bundleIdentifier = settings?.app?.bundleIdentifier;

    const hasAppStoreUrl = isSet(appStoreUrl);
    const hasPlayStoreUrl = isSet(playStoreUrl);
    const hasBundleIdentifier = isSet(bundleIdentifier);
    const isConfigured = hasAppStoreUrl && hasPlayStoreUrl && hasBundleIdentifier;

    const initialValues = React.useMemo<FormValues>(
        () => ({
            appStoreUrl: appStoreUrl ?? '',
            playStoreUrl: playStoreUrl ?? '',
            bundleIdentifier: bundleIdentifier ?? ''
        }),
        [appStoreUrl, playStoreUrl, bundleIdentifier]
    );

    const handleSave = React.useCallback(
        async (values: FormValues) => {
            try {
                await new LocationSettingsApi().updatePublicAndPrivateSettings(
                    {
                        app: {
                            appStoreUrl: values.appStoreUrl,
                            playStoreUrl: values.playStoreUrl,
                            bundleIdentifier: values.bundleIdentifier
                        }
                    },
                    undefined,
                    true
                );
                dispatch(getPublicAndPrivateSettings(claims));
                return true;
            } catch (error) {
                logger.error(`Error updating app store details: ${JSON.stringify(error)}`);
                return false;
            }
        },
        [claims, dispatch]
    );

    const renderFields = React.useCallback(
        (isEditing: boolean) => (
            <Stack spacing={2}>
                <LabeledField label="App Store URL">
                    <TextFormField
                        name="appStoreUrl"
                        disabled={!isEditing}
                        placeholder="—"
                        showHelperText={isEditing}
                    />
                </LabeledField>
                <LabeledField label="Play Store URL">
                    <TextFormField
                        name="playStoreUrl"
                        disabled={!isEditing}
                        placeholder="—"
                        showHelperText={isEditing}
                    />
                </LabeledField>
                <LabeledField label="Bundle Identifier" buildTime>
                    <TextFormField
                        name="bundleIdentifier"
                        disabled={!isEditing}
                        placeholder="—"
                        showHelperText={isEditing}
                    />
                </LabeledField>
            </Stack>
        ),
        []
    );

    const renderReadOnlyRows = React.useCallback(
        () => (
            <Stack divider={<Divider />}>
                <FieldStatusRow label="App Store URL" isSet={hasAppStoreUrl} />
                <FieldStatusRow label="Play Store URL" isSet={hasPlayStoreUrl} />
                <FieldStatusRow label="Bundle Identifier" isSet={hasBundleIdentifier} />
            </Stack>
        ),
        [hasAppStoreUrl, hasPlayStoreUrl, hasBundleIdentifier]
    );

    return (
        <EditableSettingsSection<FormValues>
            title="App store details"
            subtitle="Synced with settings. This is required to begin distributing app links."
            readOnlySubtitle="Managed by your Owner or Pepper Admin. Values are hidden."
            isConfigured={isConfigured}
            isReadOnly={isReadOnly}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSave={handleSave}
            renderFields={renderFields}
            renderReadOnlyRows={renderReadOnlyRows}
        />
    );
};
